// Enhanced content renderer - handles both HTML and legacy Markdown content

// Encode mermaid source to base64 (utf-8 safe) for a data attribute
function encodeMermaid(code) {
  try {
    return btoa(unescape(encodeURIComponent(code)));
  } catch (e) {
    try { return btoa(code); } catch (e2) { return ''; }
  }
}

// Detect if content is HTML (from WYSIWYG editor) vs old Markdown
function isHtmlContent(content) {
  if (!content) return false;
  return /<(p|h[1-6]|div|ul|ol|blockquote|img|hr|pre|table)\b/i.test(content);
}

// Render HTML content with proper styling for the blog
function renderHtmlContent(html) {
  if (!html) return '';

  let output = html;

  // Convert mermaid code blocks into diagram containers
  output = output.replace(/<pre><code class="language-mermaid">([\s\S]*?)<\/code><\/pre>/gi, (m, code) => {
    const decoded = code
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&amp;/g, '&');
    return `<div class="mermaid-diagram not-prose" data-mermaid-src="${encodeMermaid(decoded)}"></div>`;
  });

  // Also convert raw ```mermaid fenced blocks that end up as literal text inside HTML
  // content (e.g. a diagram added to a WYSIWYG/HTML post). Handles fences wrapped in
  // <p>/<pre> tags and newlines stored as <br> or paragraph breaks.
  output = output.replace(/(?:<pre>)?(?:<p>)?\s*```[ \t]*mermaid[ \t]*(?:<br\s*\/?>|\n|\r)?([\s\S]*?)```\s*(?:<\/p>)?(?:<\/pre>)?/gi, (m, code) => {
    const decoded = code
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p>/gi, '\n')
      .replace(/<\/?[a-z][^>]*>/gi, '')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/\r/g, '')
      .trim();
    return `<div class="mermaid-diagram not-prose" data-mermaid-src="${encodeMermaid(decoded)}"></div>`;
  });

  // Convert markdown tables that were pasted as literal text into a single
  // <p> (the WYSIWYG editor collapses the rows, so they never reach the
  // markdown table parser). Rows are reconstructed using the column count
  // from the |:---|:---| separator row.
  output = output.replace(/<p>([\s\S]*?)<\/p>/gi, (m, inner) => {
    const pipes = (inner.match(/\|/g) || []).length;
    if (pipes >= 6 && /\|\s*:?-{3,}:?\s*\|/.test(inner)) {
      const table = buildInlineTable(inner);
      if (table) return table;
    }
    return m;
  });

  // Strip <p> wrappers from inside <li> elements
  output = output.replace(/<li><p>([\s\S]*?)<\/p><\/li>/gi, '<li>$1</li>');

  // Ensure images with data-size/data-align render with proper styles
  // (TipTap stores these as attributes; make sure inline styles are applied)
  output = output.replace(/<img([^>]*)>/gi, (match, attrs) => {
    // If already has style with width, leave it
    if (/style=/.test(attrs)) return match;

    // Check for data attributes and build style
    const sizeMatch = attrs.match(/data-size="([^"]*)"/);
    const alignMatch = attrs.match(/data-align="([^"]*)"/);

    if (sizeMatch || alignMatch) {
      const size = sizeMatch ? sizeMatch[1] : '100%';
      const align = alignMatch ? alignMatch[1] : 'center';
      let style = `max-width: ${size}; width: ${size};`;
      if (align === 'left') {
        style += ' float: left; margin-right: 1rem; margin-bottom: 0.5rem;';
      } else if (align === 'right') {
        style += ' float: right; margin-left: 1rem; margin-bottom: 0.5rem;';
      } else {
        style += ' margin-left: auto; margin-right: auto; display: block;';
      }
      return `<img${attrs} style="${style}">`;
    }
    return match;
  });

  // Add loading="lazy" and rounded class to images that don't have them
  output = output.replace(/<img(?![^>]*loading=)([^>]*)>/gi, '<img loading="lazy"$1>');

  return output;
}

// Legacy Markdown parser (for old posts stored in markdown format)
export function formatContent(content) {
  if (!content) return '';

  // If content is HTML, render it directly with styling
  if (isHtmlContent(content)) {
    return renderHtmlContent(content);
  }

  // --- Legacy Markdown parsing below ---

  // Store code blocks to protect them from other processing
  const codeBlocks = [];
  const inlineCode = [];
  const imageBlocks = [];

  // Extract fenced code blocks first (```code```)
  let html = content.replace(/```(\w*)\n([\s\S]*?)```/g, (match, lang, code) => {
    const index = codeBlocks.length;
    codeBlocks.push({ lang, code: code.trim() });
    return `XXCODEBLKX${index}XXCODEBLKX`;
  });

  // Extract inline code (`code`)
  html = html.replace(/[`\u2018\u2019]([^`\u2018\u2019]+)[`\u2018\u2019]/g, (match, code) => {
    const index = inlineCode.length;
    inlineCode.push(code);
    return `XXINLINEX${index}XXINLINEX`;
  });

  // Extract image tags [IMG:url|size:50%|align:center]
  html = html.replace(/\[IMG:([^|]+)\|size:([^|]+)\|align:([^\]]+)\]/g, (match, url, size, align) => {
    let alignStyle = '';
    let containerClass = 'my-4';
    let isFloat = false;

    if (align === 'left') {
      alignStyle = 'float: left; margin: 0.5rem 1rem 0.5rem 0;';
      isFloat = true;
    } else if (align === 'right') {
      alignStyle = 'float: right; margin: 0.5rem 0 0.5rem 1rem;';
      isFloat = true;
    } else {
      alignStyle = 'margin-left: auto; margin-right: auto; display: block;';
      containerClass = 'my-4 text-center';
    }

    const index = imageBlocks.length;
    imageBlocks.push({ url, size, alignStyle, containerClass, isFloat });
    return `XXIMGBLKX${index}XXIMGBLKX`;
  });

  // Also handle simple [IMG:url] without size/align
  html = html.replace(/\[IMG:([^\]|]+)\]/g, (match, url) => {
    const index = imageBlocks.length;
    imageBlocks.push({
      url,
      size: '100%',
      alignStyle: 'margin-left: auto; margin-right: auto; display: block;',
      containerClass: 'my-4 text-center',
      isFloat: false
    });
    return `XXIMGBLKX${index}XXIMGBLKX`;
  });

  // Escape HTML (but not our placeholders)
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Process line by line for block elements
  const lines = html.split('\n');
  const output = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Horizontal rules
    if (/^(-{3,}|_{3,}|\*{3,})$/.test(trimmed)) {
      output.push('<hr class="my-6 border-border" />');
      i++;
      continue;
    }

    // Headers
    const headerMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const text = headerMatch[2];
      const styles = {
        1: 'text-3xl font-bold mt-6 mb-3',
        2: 'text-2xl font-semibold mt-5 mb-2',
        3: 'text-xl font-semibold mt-4 mb-2',
        4: 'text-lg font-semibold mt-4 mb-2',
        5: 'text-base font-semibold mt-3 mb-1',
        6: 'text-sm font-semibold mt-3 mb-1'
      };
      output.push(`<h${level} class="${styles[level]}">${text}</h${level}>`);
      i++;
      continue;
    }

    // Blockquotes
    if (trimmed.startsWith('&gt; ')) {
      const quoteLines = [];
      while (i < lines.length && lines[i].trim().startsWith('&gt; ')) {
        quoteLines.push(lines[i].trim().replace(/^&gt; /, ''));
        i++;
      }
      output.push(`<blockquote class="border-l-4 border-primary pl-4 italic my-3 text-muted-foreground">${quoteLines.join('<br>')}</blockquote>`);
      continue;
    }

    // Tables
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }
      output.push(buildTable(tableLines));
      continue;
    }

    // Unordered lists
    if (/^[\-\*] /.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^[\-\*] /.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[\-\*] /, ''));
        i++;
      }
      output.push(`<ul class="list-disc pl-6 mb-2">${items.map(item => `<li>${item}</li>`).join('')}</ul>`);
      continue;
    }

    // Ordered lists
    if (/^\d+\. /.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\. /.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\. /, ''));
        i++;
      }
      output.push(`<ol class="list-decimal pl-6 mb-2">${items.map(item => `<li>${item}</li>`).join('')}</ol>`);
      continue;
    }

    // Empty lines = paragraph break
    if (trimmed === '') {
      output.push('</p><p>');
      i++;
      continue;
    }

    // Regular text line
    output.push(line);
    i++;
  }

  html = output.join('\n');

  // Inline formatting
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
  html = html.replace(/~~([^~]+)~~/g, '<del>$1</del>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline">$1</a>');
  html = html.replace(/\n/g, '<br>');

  // Restore code blocks
  codeBlocks.forEach((block, index) => {
    if ((block.lang || '').toLowerCase() === 'mermaid') {
      html = html.replace(
        `XXCODEBLKX${index}XXCODEBLKX`,
        `</p><div class="mermaid-diagram not-prose" data-mermaid-src="${encodeMermaid(block.code)}"></div><p>`
      );
      return;
    }
    const langClass = block.lang ? ` language-${block.lang}` : '';
    const escapedCode = block.code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    html = html.replace(
      `XXCODEBLKX${index}XXCODEBLKX`,
      `</p><pre class="bg-muted p-4 rounded-lg overflow-x-auto my-4${langClass}"><code>${escapedCode}</code></pre><p>`
    );
  });

  // Restore inline code
  inlineCode.forEach((code, index) => {
    const escapedCode = code
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    html = html.replace(
      `XXINLINEX${index}XXINLINEX`,
      `<code class="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">${escapedCode}</code>`
    );
  });

  // Restore image blocks
  imageBlocks.forEach((block, index) => {
    const isApiWebp = /\/api\/uploads\/[^?]+\.webp$/i.test(block.url);
    const responsive = isApiWebp
      ? ` srcset="${block.url}?w=480 480w, ${block.url}?w=960 960w, ${block.url} 1600w" sizes="(max-width: 768px) 100vw, 800px"`
      : '';
    const imgTag = `<img src="${block.url}"${responsive} style="${block.alignStyle} max-width: ${block.size}; width: ${block.size};" alt="" class="post-img rounded-lg" loading="lazy" />`;
    // Float (left/right) images stay inline so two can share a single line;
    // centered images keep their own block container.
    const imgHtml = block.isFloat
      ? imgTag
      : `</p><div class="${block.containerClass}">${imgTag}</div><p>`;
    html = html.replace(
      new RegExp(`XXIMGBLKX${index}XXIMGBLKX`),
      imgHtml
    );
  });

  // Wrap in paragraph and clean up
  html = `<p>${html}</p>`;
  html = html
    .replace(/<p><\/p>/g, '')
    .replace(/<p>\s*<\/p>/g, '')
    .replace(/<p><br><\/p>/g, '')
    .replace(/<p><br>/g, '<p>')
    .replace(/<br><\/p>/g, '</p>')
    .replace(/<p>\s*(<h[1-6])/g, '$1')
    .replace(/(<\/h[1-6]>)\s*<\/p>/g, '$1')
    .replace(/<p>\s*(<hr)/g, '$1')
    .replace(/(\/>\s*)<\/p>/g, '$1')
    .replace(/<p>\s*(<ul|<ol|<blockquote|<pre|<div)/g, '$1')
    .replace(/(<\/ul>|<\/ol>|<\/blockquote>|<\/pre>|<\/div>)\s*<\/p>/g, '$1');

  // Clear any trailing floated images so they don't overlap following sections
  html += '<div style="clear: both;"></div>';

  return html;
}

// Rebuild a markdown table from pipe-delimited text that lost its line breaks
// (pasted into the WYSIWYG editor as a single paragraph). Cell HTML such as
// <strong>/<em> is preserved. Returns null if it doesn't look like a table.
function buildInlineTable(inner) {
  let tokens = inner.split('|').map(t => t.trim());
  if (tokens.length && tokens[0] === '') tokens.shift();
  if (tokens.length && tokens[tokens.length - 1] === '') tokens.pop();

  const isSep = t => /^:?-{3,}:?$/.test(t);

  // Column count = longest consecutive run of separator cells.
  let N = 0, run = 0;
  for (const t of tokens) {
    if (isSep(t)) { run++; if (run > N) N = run; } else run = 0;
  }
  if (N < 1) return null;

  // Walk the tokens N cells at a time, skipping the single empty boundary
  // token that sits between two concatenated rows.
  const rows = [];
  let i = 0;
  while (i < tokens.length) {
    rows.push(tokens.slice(i, i + N));
    i += N;
    if (i < tokens.length) i += 1;
  }
  if (rows.length < 2) return null;

  let html = '<div class="overflow-x-auto my-4"><table class="min-w-full border border-border rounded-lg"><thead><tr>';
  rows[0].forEach(cell => {
    html += `<th class="px-4 py-2 text-left font-semibold bg-muted">${cell}</th>`;
  });
  html += '</tr></thead><tbody>';
  for (let r = 2; r < rows.length; r++) {
    html += '<tr>';
    rows[r].forEach(cell => {
      html += `<td class="px-4 py-2 border-t border-border">${cell}</td>`;
    });
    html += '</tr>';
  }
  html += '</tbody></table></div>';
  return html;
}

// Build HTML table from markdown table lines
function buildTable(lines) {
  if (lines.length < 2) return lines.join('\n');

  // Split a "| a | b | c |" row, dropping only the outer empties from the
  // leading/trailing pipe so genuine empty cells (continuation rows) are kept.
  const splitRow = (line) => {
    const parts = line.split('|').map(c => c.trim());
    if (parts.length && parts[0] === '') parts.shift();
    if (parts.length && parts[parts.length - 1] === '') parts.pop();
    return parts;
  };

  let html = '<div class="overflow-x-auto my-4"><table class="min-w-full border border-border rounded-lg">';

  const headerCells = splitRow(lines[0]);
  html += '<thead><tr>';
  headerCells.forEach(cell => {
    html += `<th class="px-4 py-2 text-left font-semibold bg-muted">${cell}</th>`;
  });
  html += '</tr></thead><tbody>';

  for (let i = 2; i < lines.length; i++) {
    const cells = splitRow(lines[i]);
    html += '<tr>';
    cells.forEach(cell => {
      html += `<td class="px-4 py-2 border-t border-border">${cell}</td>`;
    });
    html += '</tr>';
  }

  html += '</tbody></table></div>';
  return html;
}
